import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { MediaUploader } from "./MediaUploader";
import { MediaCard } from "./MediaCard";

interface MediaBrowserProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (imageUrl: string, media: any) => void;
}

export const MediaBrowser = ({ open, onOpenChange, onSelect }: MediaBrowserProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<any>(null);

  const { data: mediaFiles, refetch } = useQuery({
    queryKey: ['media-library'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_library')
        .select(`
          id, filename, storage_path, file_size, mime_type,
          alt_text, title, width, height,
          thumbnail_path, medium_path, large_path,
          original_size, compressed_size, compression_ratio,
          is_public, folder_id, created_at, updated_at,
          media_tag_relations (
            media_tags (
              name
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process to extract tags
      return data?.map(media => ({
        ...media,
        tags: media.media_tag_relations?.map((rel: any) => rel.media_tags.name) || []
      }));
    },
    enabled: open,
  });

  const filteredMedia = mediaFiles?.filter(media =>
    media.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
    media.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = () => {
    if (selectedMedia) {
      const imageUrl = supabase.storage
        .from('media-library')
        .getPublicUrl(selectedMedia.storage_path).data.publicUrl;
      
      onSelect(imageUrl, selectedMedia);
      setSelectedMedia(null);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Select or Upload Media</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="library" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="library">Media Library</TabsTrigger>
            <TabsTrigger value="upload">Upload New</TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="flex-1 overflow-auto mt-4">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search media..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {filteredMedia && filteredMedia.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredMedia.map((media) => (
                  <MediaCard
                    key={media.id}
                    media={media}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    selectable
                    selected={selectedMedia?.id === media.id}
                    onSelect={setSelectedMedia}
                    tags={media.tags}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                {searchQuery ? 'No media found matching your search' : 'No media files yet. Upload some to get started!'}
              </div>
            )}

            {selectedMedia && (
              <div className="mt-4 pt-4 border-t flex justify-end">
                <Button onClick={handleSelect}>
                  Insert Selected Image
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="upload" className="mt-4">
            <MediaUploader onUploadComplete={() => {
              refetch();
            }} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
